import type { Session } from 'better-auth/types'
import { createContext, useContext, useEffect, type PropsWithChildren, useMemo, useReducer } from 'react'
import { Navigate } from 'react-router-dom'

import PageLoader from '@components/PageLoader'
import { authClient } from '@connectors/AuthClient'
import { MyUserService } from '@services/UserService'

import type { User } from '../types'

type AuthParams = {
    user: User | null
    isReady: boolean
    isAdmin: boolean
    logout: () => void
}

const AuthContext = createContext<AuthParams>({
    user: null,
    isReady: false,
    isAdmin: false,
    logout: () => {}
})

type UserState = {
    session: Session | null
    user: User | null
    isAdmin: boolean
    isReady: boolean
}
const initialState = {
    user: null,
    session: null,
    isAdmin: false,
    isReady: false
}

function reducer(state: UserState, action: any): UserState {
    switch (action.type) {
        case 'COMPLETE':
            return {
                session: action.session,
                user: action.user,
                isAdmin: action.isAdmin,
                isReady: true
            }
        case 'LOGOUT':
            return {
                user: null,
                session: null,
                isAdmin: false,
                isReady: false
            }
        default:
            return state
    }
}

export const AuthProvider = ({ children }: PropsWithChildren) => {
    const { data: session, isPending } = authClient.useSession()
    const [userState, dispatch] = useReducer(reducer, initialState)

    const fetchUser = async () => {
        try {
            const dbUser = await MyUserService.my()
            dispatch({ type: 'COMPLETE', user: dbUser, session, isAdmin: dbUser.role === 'ADMIN' })
        } catch (err) {
            dispatch({ type: 'COMPLETE', user: null, session, isAdmin: false })
        }
    }

    useEffect(() => {
        if (isPending) {
            return
        }
        fetchUser()
    }, [isPending])

    const logout = async () => {
        try {
            await authClient.signOut({}, { throw: true })
        } catch {
            // Still clear local state so the user isn't stuck on the logout page
        }
        dispatch({ type: 'LOGOUT' })
    }

    const value = useMemo(() => {
        return {
            user: userState.user,
            isAdmin: userState.isAdmin,
            isReady: userState.isReady,
            logout
        }
    }, [userState])

    // TODO: need to add loading here somewhat?
    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
    return useContext(AuthContext)
}

export const ProtectedRoute = ({ children }: PropsWithChildren) => {
    const { user, isAdmin, isReady } = useAuth()

    // If there is a current user it will render the passed down component
    if (user && isAdmin) {
        return children
    }

    if (user && isReady && !isAdmin) {
        throw new Error('You do not have access to this site')
    }

    // TODO: Handle global loading here?
    if (!isReady) {
        return <PageLoader />
    }

    // Otherwise redirect to the login route
    return <Navigate to="/login" />
}
